import { ReadonlyArray } from "@morphism/fp";
import { heapEvent } from "@equipmentshare/heaper";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import { Dashboard } from "~/Dashboard";
import * as Data from "~/Data";
import { Distance } from "~/Distance";
import { DVIR, DVIRs } from "~/DVIR";
import * as ELD from "~/ELD";
import * as ErrorMessage from "~/ErrorMessage";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as Link from "~/Link";
import * as Loading from "~/Loading";
import * as Log from "~/Log";
import { Statistic } from "~/Statistic";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

export function OverallSummary() {
  return (
    <Dashboard.Section>
      <OverallSummary.Header />
      <Dashboard.Row>
        <Layout.Column.Component xs={8}>
          <Layout.Block.Component all>
            <OverallSummary.DVIRCount />
          </Layout.Block.Component>
        </Layout.Column.Component>
        <Layout.Column.Component xs={8}>
          <Layout.Block.Component all>
            <OverallSummary.UnassignedLogs />
          </Layout.Block.Component>
        </Layout.Column.Component>
        <Layout.Column.Component xs={8}>
          <Layout.Block.Component all>
            <OverallSummary.ELDSummary />
          </Layout.Block.Component>
        </Layout.Column.Component>
      </Dashboard.Row>
    </Dashboard.Section>
  );
}

export namespace OverallSummary {
  export function Header() {
    return (
      <Layout.Section.Header.Component>
        <Layout.Section.Header.Title>
          <Header.LoadingIcon />
          <Header.Interval />
        </Layout.Section.Header.Title>
      </Layout.Section.Header.Component>
    );
  }

  export namespace Header {
    export const LoadingIcon = () =>
      pipe(
        Dashboard.Interval.use(),
        (interval) => [
          DVIRs.Summary.use(interval).loading,
          Data.Remote.isLoading(ELD.useStore().latest),
          Log.Unassigned.isLoading(),
        ],
        (loading) =>
          !loading.includes(true) ? null : (
            <Layout.Inline.Component right>
              <Loading.Icon.Component />
            </Layout.Inline.Component>
          )
      );

    export const Interval = () => (
      <Time.Interval
        {...Dashboard.Interval.use()}
        Duration={() => null}
        Start={(dateTime) => <>{Time.DateTime.toDateOnlyMMMDYYYY(dateTime)}</>}
        Separator={() => <> – </>}
        Stop={(dateTime) => <>{Time.DateTime.toDateOnlyMMMDYYYY(dateTime)}</>}
      />
    );
  }

  export function DVIRCount() {
    const navigation = DVIR.Route.use();
    return pipe(
      DVIRs.Summary.use(Dashboard.Interval.use()),
      ({ data }) => data,
      ReadonlyArray.reduce(
        {
          count: 0,
          defects: {
            resolved: 0,
            unresolved: 0,
            none: 0,
          },
        },
        (accumulator, summary) =>
          ({
            count: accumulator.count + summary.totalDVIRs,
            defects: {
              resolved: accumulator.defects.resolved + summary.resolvedDefects,
              unresolved:
                accumulator.defects.unresolved + summary.unresolvedDefects,
              none:
                accumulator.defects.none +
                (summary.resolvedDefects || summary.unresolvedDefects || 0),
            },
          } as DVIRs.Statistics)
      ),
      (statistics) => (
        <>
          <Statistic
            Title={() => <>DVIR Defects</>}
            Value={() => <>{statistics.count}</>}
          />
          <ViewDetails
            onClick={() => {
              heapEvent("E-Logs - Fleet - Click - View Details DVIR Defects", {
                value: `${statistics.count}`,
              });
              navigation.go();
            }}
          />
          <Layout.Row.Component>
            <Layout.Column.Component xs={12}>
              <DVIRCount.Resolved {...statistics} />
            </Layout.Column.Component>
            <Layout.Column.Component xs={12}>
              <DVIRCount.Unresolved {...statistics} />
            </Layout.Column.Component>
          </Layout.Row.Component>
        </>
      )
    );
  }

  export namespace DVIRCount {
    export function Resolved({ defects }: DVIRs.Statistics) {
      return (
        <Statistic
          small
          Title={Resolved.Title}
          Value={() => <>{defects.resolved}</>}
        />
      );
    }

    export namespace Resolved {
      export const Title = () => (
        <>
          <Icon.CheckCircle color={Color.green()} />
          <Layout.Inline.Component left>Resolved</Layout.Inline.Component>
        </>
      );
    }

    export const Unresolved = ({ defects }: DVIRs.Statistics) => (
      <Statistic
        small
        Value={() => <>{defects.unresolved}</>}
        Title={() => (
          <>
            <Typography.Component color={ErrorMessage.Danger.color()}>
              <Icon.TimesCircle />
            </Typography.Component>
            <Layout.Inline.Component left>Unresolved</Layout.Inline.Component>
          </>
        )}
      />
    );
  }

  export function UnassignedLogs() {
    const count = Log.Unassigned.Count.use();
    const navigation = Log.Unassigned.Route.useDefault();
    return (
      <>
        <Statistic
          Title={() => <>Unassigned Time</>}
          Value={() => <UnassignedLogs.Value {...count} />}
        />
        <ViewDetails onClick={() => {
          heapEvent("E-Logs - Fleet - Click - View Details Unassigned Time", {
            value: `${UnassignedLogs.toString(count)}`,
          });
          navigation.go();
        }} />
        <Layout.Row.Component>
          <UnassignedLogs.TotalDistance {...count} />
          <UnassignedLogs.Asset {...count} />
        </Layout.Row.Component>
      </>
    );
  }

  export namespace UnassignedLogs {

    export const toString = (count: Log.Unassigned.Count.Count) => {
      const days = Math.floor(Time.Duration.toDays(count));
      const time = Time.Duration.toHMMSS(count);

      const daysFormatted = days >= 1000
        ? "1,000+ days"
        : days > 1
        ? `${days} days `
        : days > 0
        ? `${days} day `
        : "";

        return `${daysFormatted}${time}`
    }

    export const Value = (count: Log.Unassigned.Count.Count) => {
      const days = Math.floor(Time.Duration.toDays(count));
      const time = Time.Duration.toHMMSS(count);
      return days >= 1000 ? (
        <>1,000+ days</>
      ) : (
        <>
          {days > 0 ? <>{days} days </> : null}
          {time}
        </>
      );
    };

    export const TotalDistance = (count: Log.Unassigned.Count.Count) => (
      <Layout.Column.Component xs={12}>
        <Statistic
          small
          Title={() => <>Distance</>}
          Value={() => <Distance {...count} />}
        />
      </Layout.Column.Component>
    );

    export const Asset = ({ assets }: Log.Unassigned.Count.Count) => (
      <Layout.Column.Component xs={12}>
        <Statistic
          small
          Title={() => <>Assets</>}
          Value={() => <>{assets}</>}
        />
      </Layout.Column.Component>
    );
  }

  export function ELDSummary() {
    const count = ELD.Count.use();
    const navigation = ELD.Route.use();
    return (
      <>
        <Statistic
          Title={() => <>ELD Submissions</>}
          Value={() => <>{ELD.Count.total(count)}</>}
        />
        <ViewDetails onClick={() => {
          heapEvent("E-Logs - Fleet - Click - View Details ELD Submissions", {
            value: `${ELD.Count.total(count)}`,
          });
          navigation.go();
        }} />
        <Layout.Row.Component>
          <ELDSummary.Successful {...count} />
          <ELDSummary.Unsuccessful {...count} />
        </Layout.Row.Component>
      </>
    );
  }

  export namespace ELDSummary {
    export const Successful: Component<ELD.Count.Count> = ({ successful }) => (
      <Layout.Column.Component xs={12}>
        <Statistic
          small
          Title={Title}
          Value={() => (
            <Typography.Component
              color={successful >= 1 ? Color.green() : undefined}
            >
              {successful}
            </Typography.Component>
          )}
        />
      </Layout.Column.Component>
    );

    const Title = () => (
      <>
        <Typography.Component color={Color.green()}>
          <Icon.CheckCircle />
        </Typography.Component>
        <Layout.Inline.Component left>Successful</Layout.Inline.Component>
      </>
    );

    export const Unsuccessful = ({ unsuccessful }: ELD.Count.Count) => (
      <Layout.Column.Component xs={12}>
        <Statistic
          small
          Title={() => (
            <>
              <Typography.Component color={ErrorMessage.Danger.color()}>
                <Icon.TimesCircle />
              </Typography.Component>
              <Layout.Inline.Component left>
                Unsuccessful
              </Layout.Inline.Component>
            </>
          )}
          Value={() => <>{unsuccessful}</>}
        />
      </Layout.Column.Component>
    );
  }

  function ViewDetails(props: Link.Props) {
    return (
      <ViewDetails.Container bottom>
        <Link.ViewDetails.Component {...props} />
      </ViewDetails.Container>
    );
  }

  namespace ViewDetails {
    export const Container = styled(Layout.Block.Component)`
      text-align: center;
    `;
  }
}
